import { Ayah } from '@app-types/KuranTypes'
import { useAuth } from '@contexts/AuthContext'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { IoAddCircleOutline, IoCloseCircle } from 'react-icons/io5'

const RECENT_TAGS_KEY = 'recentAyahTags'

type AyahCollectionProps = {
  ayah: Ayah
  onFormVisibilityChange?: (isVisible: boolean) => void
  showForm?: boolean
}

const AyahCollection: React.FC<AyahCollectionProps> = ({
  ayah,
  onFormVisibilityChange,
  showForm = false,
}) => {
  const { user } = useAuth()
  const router = useRouter()
  const [isSaved, setIsSaved] = useState(false)
  const [savedAyahId, setSavedAyahId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [recentTags, setRecentTags] = useState<string[]>([])

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_TAGS_KEY)
      if (stored) {
        setRecentTags(JSON.parse(stored))
      }
    } catch (error) {
      console.error('Error loading recent tags:', error)
    }
  }, [])

  const saveToCollection = async () => {
    if (!user) {
      toast.error('Koleksiyona eklemek için giriş yapmalısınız')
      return
    }

    try {
      const response = await fetch('/api/ayahCollection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: router.query.page || null,
          userId: user.uid,
          ayah: ayah,
          surahNumber: ayah.surah.id,
          ayahNumber: ayah.verse_number,
          notes: notes,
          tags: tags,
        }),
      })

      if (response.ok) {
        const savedAyah = await response.json()

        if (tags.length > 0) {
          const latestUniqueTags = Array.from(new Set([...recentTags, ...tags].reverse())).reverse()
          const updatedTags = latestUniqueTags.slice(-3)
          setRecentTags(updatedTags)
          try {
            localStorage.setItem(RECENT_TAGS_KEY, JSON.stringify(updatedTags))
          } catch (error) {
            console.error('Error saving recent tags:', error)
          }
        }

        setIsSaved(true)
        setSavedAyahId(savedAyah.id)
        setNotes('')
        setTags([])
        setTagInput('')
        onFormVisibilityChange?.(false)
        toast.success('Koleksiyona eklendi')
      } else {
        toast.error('Bir hata oluştu')
      }
    } catch (error) {
      console.error('Error adding to collection:', error)
      toast.error('Bir hata oluştu')
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleCancel = () => {
    setNotes('')
    setTags([])
    setTagInput('')
    onFormVisibilityChange?.(false)
  }

  if (!showForm) return null

  return (
    <div className="w-full bg-white dark:bg-gray-800 shadow-md p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
      <textarea
        className="bg-gray-100 dark:bg-gray-800 p-2 border border-gray-300 focus:border-green-500 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-green-500 w-full text-gray-800 dark:text-gray-200 text-sm"
        placeholder="Notunuzu buraya yazın..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
      />

      {/* Tag input section */}
      <div className="mt-2">
        <div className="flex items-center">
          <input
            type="text"
            className="input-field"
            placeholder="Etiket ekle..."
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="ml-2 text-green-500 hover:text-green-600 dark:hover:text-green-400"
            onClick={handleAddTag}
            title="Etiket ekle"
            aria-label="Etiket ekle"
          >
            <IoAddCircleOutline size={24} />
          </button>
        </div>

        {recentTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs text-muted flex items-center mr-1">Son kullanılan:</span>
            {recentTags.map((tag, index) => (
              <button
                key={index}
                className="bg-surface-subtle text-muted px-2 py-1 rounded-md text-xs cursor-pointer hover:bg-surface-strong transition-colors"
                onClick={() => {
                  if (!tags.includes(tag)) {
                    setTags([...tags, tag])
                  }
                }}
                title={`Etiket ekle: ${tag}`}
                aria-label={`Etiket ekle: ${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Display tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map((tag, index) => (
              <div
                key={index}
                className="flex items-center bg-green-100 dark:bg-green-900 px-2 py-1 rounded-md text-green-800 dark:text-green-200 text-sm"
              >
                <span>{tag}</span>
                <button
                  className="ml-1 text-green-700 hover:text-red-500 dark:hover:text-red-400 dark:text-green-300"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Etiketi kaldır: ${tag}`}
                >
                  <IoCloseCircle size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2 mt-2">
        <button
          className="bg-white hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 text-sm transition"
          onClick={handleCancel}
        >
          İptal
        </button>
        <button
          className="bg-green-500 hover:bg-green-600 dark:hover:bg-green-700 dark:bg-green-600 px-3 py-1 rounded-md text-white text-sm transition"
          onClick={saveToCollection}
        >
          Kaydet
        </button>
      </div>
    </div>
  )
}

export default AyahCollection
